import { getAccessToken } from './auth'

const API_BASE = import.meta.env.VITE_API_BASE as string

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`API ${status}: ${detail}`)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken()
  const headers: Record<string, string> = {
    'accept': 'application/json',
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  if (token) headers.authorization = `Bearer ${token}`

  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (resp.status === 204) return undefined as T
  const text = await resp.text()
  const ctype = resp.headers.get('content-type') || ''
  const looksJson = ctype.includes('json') || text.trimStart().startsWith('{') || text.trimStart().startsWith('[')

  if (!resp.ok) {
    let detail = text.slice(0, 500)
    if (looksJson) {
      try { detail = JSON.parse(text).error || detail } catch { /* fall through */ }
    } else if (text.trimStart().startsWith('<')) {
      // CloudFront swaps 4xx/5xx for the SPA index.html — surface a clear message.
      detail = 'edge served HTML in place of the API response — check CloudFront custom-error-responses on this distribution.'
    }
    throw new ApiError(resp.status, detail)
  }

  if (!text) return undefined as T
  if (!looksJson) {
    throw new ApiError(200, 'expected JSON but got ' + (ctype || 'unknown content-type') + ' — likely a CloudFront/edge misroute')
  }
  return JSON.parse(text)
}

// ---- Types ----

export interface MessageSummary {
  message_id: string
  mailbox: string
  sender: string
  recipient: string
  delivered_to?: string
  subject: string
  snippet: string
  received_at: string
  size_bytes: number
  has_attachments: boolean
  read: boolean
  starred: boolean
  kind: 'mail' | 'dmarc-report' | 'sent'
}

export interface MessageDetail extends MessageSummary {
  headers: Record<string, string>
  body_text: string
  body_html: string
  attachments: Array<{ filename: string; content_type: string; size_bytes: number; content_b64: string | null }>
}

export interface CalEvent {
  uid: string
  dtstart: string
  dtend?: string
  summary: string
  description?: string
  location?: string
  attendees?: Array<{ email: string; name?: string; rsvp?: boolean }>
  all_day?: boolean
  status?: string
  sequence?: number
}

export interface FeedInfo {
  token: string
  url: string
  webcal_url: string
  google_add_url: string
}

// ---- Mail ----

export const listMessages = (mailbox: string, folder: 'inbox' | 'sent' | 'all' = 'inbox', limit = 50) =>
  request<{ messages: MessageSummary[]; next?: string }>(
    `/mailboxes/${encodeURIComponent(mailbox)}/messages?folder=${folder}&limit=${limit}`,
  )

export const getMessage = (messageId: string, mailbox: string) =>
  request<MessageDetail>(
    `/messages/${encodeURIComponent(messageId)}?mailbox=${encodeURIComponent(mailbox)}`,
  )

export const markMessage = (messageId: string, mailbox: string, patch: { read?: boolean; starred?: boolean }) =>
  request<MessageSummary>(
    `/messages/${encodeURIComponent(messageId)}?mailbox=${encodeURIComponent(mailbox)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  )

export const deleteMessage = (messageId: string, mailbox: string) =>
  request<void>(
    `/messages/${encodeURIComponent(messageId)}?mailbox=${encodeURIComponent(mailbox)}`,
    { method: 'DELETE' },
  )

export interface SendPayload {
  to: string[] | string
  cc?: string[]
  bcc?: string[]
  subject: string
  body_text: string
  body_html?: string
  ical?: string
  in_reply_to?: string
  references?: string
  from?: string
}

export const sendMessage = (payload: SendPayload) =>
  request<{ message_id: string; sent_at: string; from: string }>('/messages/send', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

// ---- Calendar ----

export const listEvents = (start?: string, end?: string) => {
  const qs = new URLSearchParams()
  if (start) qs.set('start', start)
  if (end) qs.set('end', end)
  const q = qs.toString()
  return request<{ events: CalEvent[] }>(`/calendar/events${q ? `?${q}` : ''}`)
}

export const createEvent = (payload: Partial<CalEvent>) =>
  request<CalEvent>('/calendar/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const updateEvent = (uid: string, dtstart: string, payload: Partial<CalEvent>) =>
  request<CalEvent>(`/calendar/events/${encodeURIComponent(uid)}?dtstart=${encodeURIComponent(dtstart)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })

export const deleteEvent = (uid: string, dtstart: string) =>
  request<void>(`/calendar/events/${encodeURIComponent(uid)}?dtstart=${encodeURIComponent(dtstart)}`, {
    method: 'DELETE',
  })

export const getFeed = () => request<FeedInfo>('/calendar/feed')
export const revokeFeed = () => request<void>('/calendar/feed', { method: 'DELETE' })

// ---- DMARC ----

export const listDmarcReports = () => request<{ reports: Array<Record<string, any>> }>('/dmarc/reports')

// ---- Tenants & mailboxes ----

export interface Tenant { slug: string; name: string; brand_color?: string }
export interface Mailbox {
  id: string
  handle: string
  env: 'prod' | 'preprod'
  owner_user_id: string | null
  address: string
  created_at: string
}
export interface ProvisionResult {
  user_id: string
  handle: string
  tenant_slug: string
  mailboxes: Mailbox[]
}
export const listTenants = () => request<{ tenants: Tenant[] }>('/admin/tenants')
export const listTenantMailboxes = (slug: string, env?: 'prod' | 'preprod') => {
  const q = env ? `?env=${env}` : ''
  return request<{ mailboxes: Mailbox[] }>(`/admin/tenants/${encodeURIComponent(slug)}/mailboxes${q}`)
}
export const provisionUser = (slug: string, payload: {
  handle: string
  contact_email: string
  display_name?: string
  envs?: Array<'prod' | 'preprod'>
}) => request<ProvisionResult>(`/admin/tenants/${encodeURIComponent(slug)}/mailboxes`, {
  method: 'POST', body: JSON.stringify(payload),
})

// ---- Me ----

export const getMe = () =>
  request<{
    sub: string
    email?: string
    name?: string
    roles?: string[]
    addresses?: string[]
    from_addresses?: string[]
  }>('/me')
